/**
 * DetailsOverlay — full-screen movie details sheet.
 *
 * Above-the-fold layout on 390×844 (Pixel 5):
 *   Hero (200px) → title+meta → ratings row → availability → actions
 * Everything below the fold: overview, director, cast, partner states.
 *
 * Usage:
 *   <DetailsOverlay token={token} seat={seat} tmdbId={id} onClose={() => setOpen(null)} />
 */
import type { TitleDetails } from "@hyggebam/shared";
import { useEffect, useRef, useState } from "react";
import { api } from "../api.ts";
import { TrailerLightbox, extractYouTubeId } from "./TrailerLightbox.tsx";

const DETAILS_HINT_KEY = "hyggebam.detailsHintSeen";
const SWIPE_DOWN_THRESHOLD = 120; // px to commit close

function ratingClass(source: string) {
  if (source === "IMDb") return "imdb";
  if (source === "Rotten Tomatoes") return "rt";
  if (source === "Metacritic") return "mc";
  return "tmdb";
}

function stateLabel(state: string): string {
  const map: Record<string, string> = {
    like: "Liked",
    later: "Later",
    never: "Not interested",
    seen_rated: "Seen (imported)",
    watched: "Watched",
    abandoned: "Abandoned",
    unseen: "Not seen yet",
  };
  return map[state] ?? state;
}

interface Props {
  token: string;
  seat: string;
  tmdbId: number;
  onClose: () => void;
  /** Called after a wishlist add/remove so callers can sync state. */
  onWishlistChange?: (tmdbId: number, inWishlist: boolean) => void;
  /** Called after markWatched so callers can react. */
  onWatched?: (tmdbId: number) => void;
}

export function DetailsOverlay({ token, seat, tmdbId, onClose, onWishlistChange, onWatched }: Props) {
  const [details, setDetails] = useState<TitleDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [ratingModal, setRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [savingWatched, setSavingWatched] = useState(false);
  const [watchedDone, setWatchedDone] = useState(false);
  const [togglingWishlist, setTogglingWishlist] = useState(false);

  // Bottom-sheet swipe-to-close: a downward drag closes the sheet from anywhere
  // that isn't actively scrolling (the hero, or the body when scrolled to top).
  const overlayRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragMode = useRef<"idle" | "close" | "scroll">("idle");
  const dragDeltaYRef = useRef(0); // single source of truth for touch handler closures
  const [dragDeltaY, setDragDeltaY] = useState(0);

  // First-time hint
  const [showHint, setShowHint] = useState(() => !localStorage.getItem(DETAILS_HINT_KEY));

  useEffect(() => {
    if (showHint) {
      localStorage.setItem(DETAILS_HINT_KEY, "1");
    }
  }, [showHint]);

  // Hide hint after 2.8s
  useEffect(() => {
    if (!showHint) return;
    const t = setTimeout(() => setShowHint(false), 2800);
    return () => clearTimeout(t);
  }, [showHint]);

  // ── Native touch swipe-to-close ─────────────────────────────────────────────
  // We attach with { passive: false } so we can call preventDefault() to
  // cancel native scrolling once we've committed to a close gesture. Pointer
  // events alone can't do this — by the time the compositor confirms a
  // downward drag it has already claimed the gesture and fires pointercancel.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    function onTouchStart(e: TouchEvent) {
      const target = e.target as Node | null;
      if (isInteractive(target)) return;
      const touch = e.touches.item(0);
      if (!touch) return;
      dragStartY.current = touch.clientY;
      dragMode.current = "idle";
    }

    function onTouchMove(e: TouchEvent) {
      if (dragStartY.current === null) return;
      const touch = e.touches.item(0);
      if (!touch) return;
      const touchY = touch.clientY;
      const delta = touchY - dragStartY.current;

      if (dragMode.current === "idle") {
        if (Math.abs(delta) < 6) return;
        const inBody = bodyRef.current?.contains(e.target as Node) ?? false;
        const atTop = (bodyRef.current?.scrollTop ?? 0) <= 0;
        if (delta > 0 && (!inBody || atTop)) {
          dragMode.current = "close";
        } else {
          dragMode.current = "scroll";
          dragStartY.current = null;
          return;
        }
      }

      if (dragMode.current === "close") {
        // Prevent native scroll so the gesture is ours.
        e.preventDefault();
        const d = Math.max(0, delta);
        dragDeltaYRef.current = d;
        setDragDeltaY(d);
      }
    }

    function onTouchEnd() {
      const closing = dragMode.current === "close" && dragDeltaYRef.current > SWIPE_DOWN_THRESHOLD;
      dragStartY.current = null;
      dragMode.current = "idle";
      dragDeltaYRef.current = 0;
      setDragDeltaY(0);
      if (closing) onClose();
    }

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  // Re-bind when onClose identity changes (parent re-renders are rare).
  // dragDeltaYRef is a ref so it doesn't need to be a dependency.
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.title(token, tmdbId)
      .then((d) => { setDetails(d); setLoading(false); })
      .catch(() => { setError("Could not load details"); setLoading(false); });
  }, [token, tmdbId]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function toggleWishlist() {
    if (!details) return;
    setTogglingWishlist(true);
    try {
      if (details.in_wishlist) {
        await api.removeWishlist(token, tmdbId);
        setDetails((d) => d ? { ...d, in_wishlist: false } : d);
        onWishlistChange?.(tmdbId, false);
      } else {
        await api.addWishlist(token, seat, tmdbId);
        setDetails((d) => d ? { ...d, in_wishlist: true } : d);
        onWishlistChange?.(tmdbId, true);
      }
    } finally {
      setTogglingWishlist(false);
    }
  }

  async function saveWatched() {
    setSavingWatched(true);
    try {
      await api.markWatched(token, seat, tmdbId, rating > 0 ? rating : undefined);
      setWatchedDone(true);
      setRatingModal(false);
      onWatched?.(tmdbId);
      // Refresh details to reflect new state
      const fresh = await api.title(token, tmdbId);
      setDetails(fresh);
    } finally {
      setSavingWatched(false);
    }
  }

  // ── Swipe-anywhere-to-close (except while scrolling) ────────────────────────
  // Don't hijack taps on interactive controls (buttons, links, inputs, stars).
  function isInteractive(el: EventTarget | null): boolean {
    return el instanceof Element && !!el.closest('button, a, input, select, textarea, iframe, [role="button"]');
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (isInteractive(e.target)) return;
    dragStartY.current = e.clientY;
    dragMode.current = "idle";
    // Capture is deferred until we know it's a close-drag (not a scroll).
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;

    if (dragMode.current === "idle") {
      if (Math.abs(delta) < 6) return; // wait for a clear direction
      const inBody = bodyRef.current?.contains(e.target as Node) ?? false;
      const atTop = (bodyRef.current?.scrollTop ?? 0) <= 0;
      // Downward drag from a non-scrolling spot (hero, or body at the top) =
      // the close gesture. Otherwise it's a scroll — bow out and let it run.
      if (delta > 0 && (!inBody || atTop)) {
        dragMode.current = "close";
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } else {
        dragMode.current = "scroll";
        dragStartY.current = null;
        return;
      }
    }

    if (dragMode.current === "close") setDragDeltaY(Math.max(0, delta));
  }

  function handlePointerUp() {
    const closing = dragMode.current === "close" && dragDeltaY > SWIPE_DOWN_THRESHOLD;
    dragStartY.current = null;
    dragMode.current = "idle";
    setDragDeltaY(0);
    if (closing) onClose();
  }

  const youtubeId = details?.trailer_url ? extractYouTubeId(details.trailer_url) : null;

  // Apply a slight downward translate while dragging to give tactile feedback.
  const overlayStyle: React.CSSProperties = dragDeltaY > 0
    ? { transform: `translateY(${dragDeltaY * 0.4}px)`, transition: "none" }
    : {};

  return (
    <>
      <div
        ref={overlayRef}
        className="details-overlay"
        data-testid="details-screen"
        role="dialog"
        aria-modal="true"
        aria-label={details?.title ?? "Movie details"}
        style={overlayStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* ── First-time swipe hint ── */}
        {showHint && (
          <div
            className="details-swipe-hint"
            data-testid="details-swipe-hint"
            aria-label="Swipe down to close"
          >
            ⌄ swipe down to close
          </div>
        )}

        {/* ── Hero ── */}
        <div
          className="details-hero"
          style={
            details?.backdrop
              ? { backgroundImage: `url(${details.backdrop})` }
              : details?.poster
                ? { backgroundImage: `url(${details.poster})` }
                : undefined
          }
        >
          <button
            className="details-hero-close"
            data-testid="details-close"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onClose}
            aria-label="Close details"
          >
            ✕
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div
          className="details-body"
          ref={bodyRef}
        >
          {loading && <div className="spinner" style={{ marginTop: 20 }} />}
          {error && <p className="sub" style={{ color: "var(--never)", marginTop: 16 }}>{error}</p>}

          {details && (
            <>
              {/* ── Title + meta ── */}
              <div className="details-title-row">
                <h2 data-testid="details-title">{details.title}</h2>
                <div className="details-meta">
                  {[
                    details.year,
                    details.runtime ? `${details.runtime} min` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  {details.genres.length > 0 && (
                    <span>
                      {" · "}
                      <span className="chips" style={{ display: "inline-flex", verticalAlign: "middle" }}>
                        {details.genres.slice(0, 3).map((g) => (
                          <span className="chip" key={g}>{g}</span>
                        ))}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {/* ── Ratings row (above the fold) ── */}
              {details.ratings.length > 0 && (
                <div className="details-ratings" data-testid="details-ratings-row">
                  {details.ratings.map((r) => (
                    <div
                      key={r.source}
                      className={`rating-badge ${ratingClass(r.source)}`}
                      data-testid="details-rating"
                      data-source={r.source}
                    >
                      <span className="rb-source">{r.source === "Rotten Tomatoes" ? "RT" : r.source}</span>
                      <span className="rb-value">{r.display}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Availability (above the fold) ── */}
              <div className="details-availability" data-testid="details-availability">
                <div className="details-avail-label">Where to watch</div>
                <div className="chips">
                  {details.available_on.length > 0 ? (
                    details.available_on.map((o) => (
                      <span className="chip svc" key={o.service}>{o.name}</span>
                    ))
                  ) : (
                    <span className="chip" style={{ color: "var(--muted)" }}>
                      Not on your services
                    </span>
                  )}
                </div>
              </div>

              {/* ── Actions (above the fold) ── */}
              <div className="details-actions">
                {youtubeId && (
                  <button
                    className="act-btn primary"
                    data-testid="trailer-button"
                    onClick={() => setTrailerOpen(true)}
                    aria-label="Watch trailer"
                  >
                    <span style={{ fontSize: 18 }}>▶</span>
                    <span>Trailer</span>
                  </button>
                )}
                <button
                  className={`act-btn${details.in_wishlist ? " active" : ""}`}
                  data-testid="details-wishlist-toggle"
                  disabled={togglingWishlist}
                  onClick={toggleWishlist}
                  aria-label={details.in_wishlist ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <span style={{ fontSize: 18 }}>{details.in_wishlist ? "♥" : "♡"}</span>
                  <span>Wishlist</span>
                </button>
                <button
                  className={`act-btn${watchedDone ? " active" : ""}`}
                  data-testid="details-mark-watched"
                  disabled={savingWatched}
                  onClick={() => { setRating(0); setRatingModal(true); }}
                  aria-label="Mark as watched"
                >
                  <span style={{ fontSize: 18 }}>✓</span>
                  <span>{watchedDone ? "Watched!" : "Watched"}</span>
                </button>
              </div>

              {/* ── Below the fold: overview, director, cast, states ── */}
              {details.overview && (
                <div className="details-section">
                  <div className="details-section-label">Overview</div>
                  <p className="details-overview">{details.overview}</p>
                </div>
              )}

              {(details.director || details.cast.length > 0) && (
                <div className="details-section">
                  {details.director && (
                    <div style={{ marginBottom: 6 }}>
                      <span className="details-section-label">Director </span>
                      <span style={{ fontSize: 13 }}>{details.director}</span>
                    </div>
                  )}
                  {details.cast.length > 0 && (
                    <div>
                      <span className="details-section-label">Cast </span>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>
                        {details.cast.slice(0, 5).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {details.states.length > 0 && (
                <div className="details-section">
                  <div className="details-section-label">Partner states</div>
                  <div className="details-states">
                    {details.states.map((ps) => (
                      <div className="details-state-row" key={ps.person_id}>
                        <span className="ds-name">{ps.display_name}</span>
                        <span className="ds-state">{stateLabel(ps.state)}</span>
                        {ps.rating && (
                          <span style={{ color: "#f5c518", fontSize: 12 }}>★ {ps.rating}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Trailer lightbox ── */}
      {trailerOpen && youtubeId && <TrailerLightbox youtubeId={youtubeId} onClose={() => setTrailerOpen(false)} />}

      {/* ── Mark watched rating modal ── */}
      {ratingModal && (
        <div className="rating-modal-bg" onClick={() => setRatingModal(false)}>
          <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Rate it (optional)</h3>
            <div className="stars-row">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <span
                  key={n}
                  className={n <= rating ? "on" : ""}
                  onClick={() => setRating(n)}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  role="button"
                >
                  ★
                </span>
              ))}
            </div>
            <button
              className="btn"
              disabled={savingWatched}
              onClick={saveWatched}
            >
              {savingWatched ? "Saving…" : "Mark as watched"}
            </button>
            <div style={{ height: 8 }} />
            <button className="btn ghost" onClick={() => setRatingModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
